import React, { useMemo, useRef } from 'react';
import FileFieldInput from '../../../components/common/FormCrafterFormRenderer/inputs/fields/FileFieldInput';
import {
  buildHcpSignupWizardAttachmentFileConfig,
  getHcpSignupWizardAttachmentFileValidationOpts,
  formatHcpFileUploadHint,
  buildHcpSignupIdDocumentFileConfig,
  validationConfigDefaults,
  educationExperienceConfigDefaults,
} from '../../../components/HcpSignup/hcpSignupDefaults';

function normalizeExistingFileDescriptor(fileLike) {
  if (!fileLike?.id) return null;
  return {
    id: fileLike.id,
    name: fileLike.originalName || fileLike.name || `Document #${fileLike.id}`,
    mimeType: fileLike.mimeType || '',
    size: fileLike.fileSize ?? fileLike.size ?? 0,
  };
}

/**
 * Single-file Apollo {@link FileFieldInput} (HCP signup wizard parity) for profile maintenance.
 */
export function ProfileWizardSingleFileField({
  id,
  label,
  localFile,
  existingServerFile,
  onFileChange,
  onClearExistingServerFile,
  disabled,
  helperText,
  showError,
  preset = 'attachment',
  validationConfig,
  requiredHint = false,
}) {
  const fileConfig = useMemo(() => {
    if (preset === 'idDocument') {
      return buildHcpSignupIdDocumentFileConfig({
        ...validationConfigDefaults,
        ...(validationConfig || {}),
      });
    }
    return buildHcpSignupWizardAttachmentFileConfig(1);
  }, [preset, validationConfig]);

  const defaultHint = useMemo(() => {
    if (preset === 'idDocument') {
      const c = { ...validationConfigDefaults, ...(validationConfig || {}) };
      return formatHcpFileUploadHint(
        c.idImageAcceptExt,
        c.idImageMaxMb,
        !!c.idImageRequired
      );
    }
    const o = getHcpSignupWizardAttachmentFileValidationOpts();
    return formatHcpFileUploadHint(o.acceptExt, o.maxMb, requiredHint);
  }, [preset, validationConfig, requiredHint]);

  const existingFiles = useMemo(() => {
    const row = normalizeExistingFileDescriptor(existingServerFile);
    if (!row || localFile instanceof File) return [];
    return [row];
  }, [existingServerFile, localFile]);

  return (
    <FileFieldInput
      id={id}
      label={label}
      columnStyle={{ width: '100%', maxWidth: '100%' }}
      value={localFile instanceof File ? [localFile] : []}
      onUpload={(uploaded) => {
        const list = Array.isArray(uploaded) ? uploaded : [uploaded];
        const next = list.find((x) => x instanceof File);
        if (!next) return;
        onFileChange?.(next);
      }}
      onDelete={() => onFileChange?.(null)}
      onServerFileDelete={
        onClearExistingServerFile
          ? async () => {
              onClearExistingServerFile();
            }
          : async () => {}
      }
      helperText={helperText ?? defaultHint}
      showError={!!showError}
      disabled={!!disabled}
      required={false}
      fileConfig={fileConfig}
      existingFiles={existingFiles}
      eagerUploadEnabled={false}
      enablePendingDelete={false}
      initialStagedFiles={[]}
    />
  );
}

/**
 * Multi-file signup-style upload (e.g. experience relevant attachments).
 */
export function ProfileWizardMultiFileField({
  id,
  label,
  localFiles,
  existingServerFiles,
  onLocalFilesChange,
  onServerFilesRemoved,
  disabled,
  helperText,
  showError,
  maxItems: maxItemsProp,
}) {
  const maxItems =
    maxItemsProp ??
    educationExperienceConfigDefaults.experience.relevantAttachmentsMax;

  const fileConfig = useMemo(
    () => buildHcpSignupWizardAttachmentFileConfig(maxItems),
    [maxItems]
  );

  const defaultHint = useMemo(() => {
    const o = getHcpSignupWizardAttachmentFileValidationOpts();
    const line = formatHcpFileUploadHint(o.acceptExt, o.maxMb, false);
    return `${line} Up to ${maxItems} files.`;
  }, [maxItems]);

  const safeLocal = Array.isArray(localFiles)
    ? localFiles.filter((f) => f instanceof File)
    : [];

  const localFilesRef = useRef(safeLocal);
  localFilesRef.current = safeLocal;

  const existingFiles = useMemo(
    () =>
      (Array.isArray(existingServerFiles) ? existingServerFiles : [])
        .map((f) => normalizeExistingFileDescriptor(f))
        .filter(Boolean),
    [existingServerFiles]
  );

  return (
    <FileFieldInput
      id={id}
      label={label}
      columnStyle={{ width: '100%', maxWidth: '100%' }}
      value={safeLocal}
      onUpload={(uploaded) => {
        const list = (Array.isArray(uploaded) ? uploaded : [uploaded]).filter(
          (x) => x instanceof File
        );
        if (!list.length) return;
        onLocalFilesChange?.([...localFilesRef.current, ...list]);
      }}
      onDelete={(file) => {
        onLocalFilesChange?.(safeLocal.filter((f) => f !== file));
      }}
      onServerFileDelete={async (ids) => {
        const idList = Array.isArray(ids) ? ids : ids != null ? [ids] : [];
        if (idList.length && onServerFilesRemoved) {
          onServerFilesRemoved(idList);
        }
      }}
      helperText={helperText ?? defaultHint}
      showError={!!showError}
      disabled={!!disabled}
      required={false}
      fileConfig={fileConfig}
      existingFiles={existingFiles}
      eagerUploadEnabled={false}
      enablePendingDelete={false}
      initialStagedFiles={[]}
    />
  );
}
